import { useState } from 'react';
import PublicFooter from '../../components/PublicFooter';
import PublicHeader from '../../components/PublicHeader';
import { resolveLeadSource } from '../../lib/analytics';

export default function LandingPage({ trackingSource }) {
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState('idle');

  async function submit(event) {
    event.preventDefault();
    const nextEmail = email.trim();
    if (!event.currentTarget.checkValidity() || !nextEmail) {
      setFormState('error');
      event.currentTarget.reportValidity();
      return;
    }

    setFormState('submitting');
    try {
      const leadsUrl =
        process.env.REACT_APP_LEADS_URL ||
        'https://nextvisit-api.unwishlist.workers.dev/api/leads';
      const response = await fetch(leadsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: nextEmail,
          source: resolveLeadSource(trackingSource) || 'landing-page',
        }),
      });

      if (!response.ok) throw new Error('Your demo request could not be sent.');
      setFormState('success');
    } catch {
      setFormState('submit-error');
    }
  }

  function updateEmail(event) {
    setEmail(event.target.value);
    if (formState !== 'idle') setFormState('idle');
  }

  const formMessage =
    formState === 'error'
      ? 'Enter a valid business email.'
      : formState === 'submit-error'
        ? 'We couldn’t send your request. Please try again.'
        : formState === 'success'
          ? "Thanks — we'll reach out to arrange your demo."
          : null;

  return (
    <main className="loyal-landing">
      <PublicHeader />

      <section className="loyal-hero" id="for-businesses">
        <div className="loyal-hero-copy">
          <h1>
            Turn first-time customers into regulars.{' '}
            <span><strong>Free</strong> to set up and use.</span>
          </h1>
          <p>
            Customers scan your QR at checkout, claim an offer for their next visit,
            and return to redeem it.
          </p>
          <ul className="loyal-steps" id="how-it-works" aria-label="How LoyalQR Basic works">
            <li>
              <span aria-hidden="true">1</span>Display your QR at checkout
            </li>
            <li>
              <span aria-hidden="true">2</span>Customers scan and claim a voucher (no app required)
            </li>
            <li>
              <span aria-hidden="true">3</span>They come back to redeem their voucher
            </li>
          </ul>
          <p className="loyal-lead-intro">
            Leave your business email below and we’ll help you set up your first
            next-visit voucher.
          </p>
          <form
            id="get-started"
            className="loyal-lead-form"
            onSubmit={submit}
            aria-busy={formState === 'submitting'}
          >
            <label htmlFor="business-email">Business email</label>
            <div>
              <input
                id="business-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                maxLength="254"
                value={email}
                onChange={updateEmail}
                onInvalid={() => setFormState('error')}
                placeholder="Your business email"
                aria-describedby={formMessage ? 'business-email-help' : undefined}
                disabled={formState === 'success'}
                required
              />
              <button
                type="submit"
                disabled={formState === 'submitting' || formState === 'success'}
              >
                {formState === 'submitting'
                  ? 'Sending…'
                  : formState === 'success'
                    ? 'Request sent'
                    : 'Get started free'}
                <span aria-hidden="true">→</span>
              </button>
            </div>
            {formMessage && (
              <small
                id="business-email-help"
                className={formState === 'success' ? 'success' : ''}
                role={formState === 'error' || formState === 'submit-error' ? 'alert' : 'status'}
                aria-live="polite"
              >
                {formMessage}
              </small>
            )}
          </form>
        </div>

        <figure className="loyal-hero-visual">
          <img
            src="/landing-demo-v2.jpg"
            srcSet="/landing-demo-v2-1000.jpg 1000w, /landing-demo-v2.jpg 1672w"
            sizes="(max-width: 980px) 100vw, 56vw"
            width="1672"
            height="941"
            loading="eager"
            fetchPriority="high"
            alt="A customer scanning a LoyalQR next-visit voucher at a café counter"
          />
        </figure>
      </section>

      <section className="loyal-network-vision" aria-labelledby="network-vision-title">
        <div className="loyal-network-inner">
          <div className="loyal-network-copy">
            <h2 id="network-vision-title">
              Let customers from other local businesses find you.
            </h2>
            <p>
              LoyalQR helps bring your own customers back—and helps customers visiting
              other participating businesses discover your offer, giving you another
              way to be found nearby.
            </p>
            <p className="loyal-network-status">
              <strong>Network benefit</strong>
              <span>Reach beyond your existing customers and be discovered nearby</span>
            </p>
            <p className="loyal-network-trust">
              Customers choose what they explore. Participating businesses do not
              exchange customer lists.
            </p>
            <a className="loyal-network-cta" href="#get-started">
              Join LoyalQR free
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14M14 7l5 5-5 5" />
              </svg>
            </a>
          </div>

          <div
            className="loyal-neighbourhood"
            role="img"
            aria-label="A customer choosing to discover an offer from another participating local business nearby"
          >
            <svg className="loyal-neighbourhood-links" viewBox="0 0 620 470" aria-hidden="true">
              <path d="M132 92 310 235 488 90" />
              <path d="M126 380 310 235 494 378" />
            </svg>

            <div className="loyal-local-business is-cafe">
              <span className="loyal-storefront" aria-hidden="true">
                <i /><i /><i />
              </span>
              <strong>Neighbourhood café</strong>
            </div>
            <div className="loyal-local-business is-bakery">
              <span className="loyal-storefront" aria-hidden="true">
                <i /><i /><i />
              </span>
              <strong>Local bakery</strong>
            </div>
            <div className="loyal-local-business is-salon">
              <span className="loyal-storefront" aria-hidden="true">
                <i /><i /><i />
              </span>
              <strong>Independent salon</strong>
            </div>
            <div className="loyal-local-business is-bookshop">
              <span className="loyal-storefront" aria-hidden="true">
                <i /><i /><i />
              </span>
              <strong>Local bookshop</strong>
            </div>

            <div className="loyal-discovery-card">
              <span>From the LoyalQR network</span>
              <strong>An offer near you</strong>
              <p>Discover another participating business after claiming or redeeming an offer.</p>
              <small>Explore this offer</small>
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}
